import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import _get from "lodash/get";
import _values from "lodash/values";
import { RootState } from "../../../../../models";
import { setActiveSubscription } from "../../../checkout/state/actions/cartActions";
import { getSubscriptions } from "../../../checkout/state/selectors/subscriptionsSelectors";
import { Header } from "../../components/atoms/Header";
import { AddNewSubscriptions } from "../../components/molecules/AddNewSubscriptions";
import { ExistingSubscriptions } from "../../components/molecules/ExistingSubscriptions";
import { prefillCustomerForm } from "../../state/actions/customerActions";
import { setValue as setPersonalDetailsValue } from "../../state/actions";
import { getCustomerExistingAccounts, getExistingSubscriptionForChange } from "../../state/selectors/customerSelectors";
import {
    initializeExistingSubscriptionPrefill,
    setExistingSubscriptionForChange,
    setSubscriptionStep,
} from "../../state/actions/subscriptionStepsActions";
import { getActiveSubscription } from "../../../checkout/state/selectors/cartSelectors";
import { isAddingSubscription as isAddingSubscriptionSelector } from "../../state/selectors/subscriptionStepsSelectors";
const Subscription = () => {
    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const dispatch = useDispatch();
    const existingSubscriptionForChange = useSelector(getExistingSubscriptionForChange);
    const subscriptions = useSelector(getSubscriptions);
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));
    const isAddingSubscription = useSelector((state: RootState) => isAddingSubscriptionSelector(state));
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const lastPrefilledPhoneLookupRef = useRef<string | null>(null);

    // When opening a subscription for edit via ?subscriptionId= URL, clear any stale sessionStorage
    // entries used by AddSubscriptionSteps to re-trigger the phone number check after a page refresh.
    // This must happen synchronously during render (before child effects run) so that
    // AddSubscriptionSteps' "re-trigger after refresh" effect does not fire spuriously.
    const subscriptionIdFromUrl = searchParams.get("subscriptionId");
    if (subscriptionIdFromUrl) {
        sessionStorage.removeItem(`showOnlyMobileTab_${subscriptionIdFromUrl}`);
        sessionStorage.removeItem(`existingPhoneNumber_${subscriptionIdFromUrl}`);
    }

    // Used for opening a sub in edit mode when clicking an item in cart
    useEffect(() => {
        const subscriptionId = searchParams.get("subscriptionId");
        const subscription = subscriptionId ? subscriptions[subscriptionId] : undefined;
        if (subscriptionId && subscription) {
            const scope = _get(subscription, "existingSubscriptionUpdateScope") as
                | "full"
                | "sim"
                | "productUser"
                | undefined;
            let stepIndex = 0;
            if (scope === "sim") {
                stepIndex = 1;
            } else if (scope === "productUser") {
                stepIndex = 2;
            }

            dispatch(setActiveSubscription(subscriptionId));
            dispatch(setSubscriptionStep(subscriptionId, stepIndex));

            const phoneNumber = _get(subscription, "phoneNumber.selectedPhoneNumber") as string | undefined;
            const existingSubscription = phoneNumber
                ? existingAccounts
                      .flatMap((account) => account.subscriptions)
                      .find((sub) => sub.phoneNumber === phoneNumber)
                : undefined;

            if (existingSubscription) {
                dispatch(
                    setExistingSubscriptionForChange(
                        scope === "sim" || scope === "productUser"
                            ? { ...existingSubscription, changeScope: scope }
                            : existingSubscription,
                    ),
                );
                // Prevent the prefill useEffect from re-running the phone number check
                // since the cart item is already fully set up from stored state.
                lastPrefilledPhoneLookupRef.current = phoneNumber ?? null;
            }

            dispatch(setPersonalDetailsValue("isCatalogListingOk", subscription?.isCatalogListingOk ?? true));
            dispatch(prefillCustomerForm(subscriptionId, false, true));
        }

        if (subscriptionId) {
            navigate({ search: "" }, { replace: true });
        }
    }, []);

    // This effect handles pre-filling the subscription from two flows:
    // 1) If started with existing flow, existingSubscriptionForChange is set in Redux state and used to prefill
    //    the looked up phone number and its subscription data.
    // 2) If clicking "Endre abonnement", "Endre SIM-kort" or "Endre bruker" on an existing subscription card, existingSubscriptionForChange is updated
    //    in Redux state, then this effect pre-fills the phone number and subscription data.
    useEffect(() => {
        if (!existingSubscriptionForChange) return;
        const lookupPhoneNumber = existingSubscriptionForChange.phoneNumber;
        if (!lookupPhoneNumber) return;
        if (!existingSubscriptionForChange.offeringCode) return;

        lastPrefilledPhoneLookupRef.current = lookupPhoneNumber;
        dispatch(initializeExistingSubscriptionPrefill(existingSubscriptionForChange, lookupPhoneNumber));
    }, [existingSubscriptionForChange, dispatch]);

    // This effect exists to support edge case
    // 1) Agent select "Velg abonnement" on an existing subscription card, which triggers pre-filling the form and setting lastPrefilledPhoneLookupRef.
    // 2) Agent delete the cart item subscription draft, which causes the pre-filled subscription to be removed from the cart and subscriptions selector, but lastPrefilledPhoneLookupRef still holds the phone number.
    // 3) Agent selects the same existing subscription card again, but since lastPrefilledPhoneLookupRef is the same as the lookup phone number, the form will not be pre-filled again, causing confusion to the agent.
    useEffect(() => {
        const lastPrefilledPhoneNumber = lastPrefilledPhoneLookupRef.current;
        if (!lastPrefilledPhoneNumber) return;

        const isLastPrefilledPhoneInCart = _values(subscriptions).some(
            (subscription) => subscription.phoneNumber?.selectedPhoneNumber === lastPrefilledPhoneNumber,
        );

        if (!isLastPrefilledPhoneInCart) {
            lastPrefilledPhoneLookupRef.current = null;
        }
    }, [subscriptions]);

    return (
        <div className="retailer-dashboard-wrapper">
            <Header text="Abonnement" />
            <div className="retailer-main-section">
                <AddNewSubscriptions />
                {!activeSubscription && !isAddingSubscription && <ExistingSubscriptions />}
            </div>
        </div>
    );
};

export default Subscription;
